import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import SellerLayout from "@/components/SellerLayout";
import { PageHeader, EmptyState, LoadingSkeleton, ConfirmDialog, FormField } from "@/components/ui-system";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Info, KeyRound, Loader2, Lock, Plus, ShieldCheck, Trash2, Users } from "lucide-react";
import { cn } from "@/lib/utils";

const NEW_ROLE = "__new__";
const SLUG_RE = /^[a-z][a-z0-9_]{1,49}$/;
// Locked system roles: read-only in this UI (the org superuser).
const LOCKED_ROLE_NAMES = new Set(["seller_admin"]);

const apiErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d?.msg || String(d)).join("; ");
  return fallback;
};

// Catalog permissions arrive as {key,label}; tolerate [key,label] pairs too.
const normalizeGroups = (groups) =>
  (Array.isArray(groups) ? groups : []).map((g) => ({
    group: g.group,
    permissions: (g.permissions || []).map((p) =>
      Array.isArray(p) ? { key: p[0], label: p[1] } : p
    ),
  }));

const slugify = (value) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/^(\d)/, "r_$1")
    .slice(0, 50);

const groupTestId = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const PermissionGroupCard = ({ group, selected, disabled, onToggleGroup, onTogglePermission }) => {
  const keys = group.permissions.map((p) => p.key);
  const selectedCount = keys.filter((k) => selected.has(k)).length;
  const groupState =
    keys.length > 0 && selectedCount === keys.length
      ? true
      : selectedCount > 0
        ? "indeterminate"
        : false;
  const gid = groupTestId(group.group);

  return (
    <Card className="border shadow-sm">
      <CardHeader className="flex flex-row items-center gap-3 space-y-0 px-4 py-3">
        <Checkbox
          id={`group-${gid}`}
          checked={groupState}
          disabled={disabled}
          onCheckedChange={(checked) => onToggleGroup(group, checked === true)}
          aria-label={`Select all ${group.group} permissions`}
          data-testid={`group-select-all-${gid}`}
        />
        <Label
          htmlFor={`group-${gid}`}
          className="cursor-pointer font-heading text-sm font-semibold text-slate-800"
        >
          {group.group}
        </Label>
        <span className="ml-auto text-xs text-slate-500 tabular-nums">
          {selectedCount}/{keys.length}
        </span>
      </CardHeader>
      <CardContent className="px-4 pb-4 pt-0">
        <div className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2 xl:grid-cols-3">
          {group.permissions.map((p) => (
            <div key={p.key} className="flex items-start gap-2 py-1">
              <Checkbox
                id={`perm-${p.key}`}
                className="mt-0.5"
                checked={selected.has(p.key)}
                disabled={disabled}
                onCheckedChange={(checked) => onTogglePermission(p.key, checked === true)}
                data-testid={`perm-checkbox-${p.key}`}
              />
              <Label htmlFor={`perm-${p.key}`} className="cursor-pointer font-normal">
                <span className="block text-sm text-slate-700">{p.label}</span>
                <span className="mt-0.5 block font-mono text-xs text-slate-400">{p.key}</span>
              </Label>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const emptyForm = () => ({
  displayName: "",
  slug: "",
  slugTouched: false,
  description: "",
  permissions: new Set(),
});

const SellerTeamRoles = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Cosmetic guard - the backend hard-locks team-role management to `seller_admin`.
  useEffect(() => {
    if (user && user.role !== "seller_admin") {
      toast.error("Only the Seller Admin can manage team roles");
      navigate("/seller");
    }
  }, [user, navigate]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [catalogRes, rolesRes] = await Promise.all([
        axios.get(`${API}/seller-roles/catalog`),
        axios.get(`${API}/seller-roles`),
      ]);
      const catalogData = catalogRes.data || {};
      setCatalog(
        normalizeGroups(Array.isArray(catalogData) ? catalogData : catalogData.seller || catalogData.platform)
      );
      setRoles(Array.isArray(rolesRes.data) ? rolesRes.data : rolesRes.data?.roles || []);
    } catch (error) {
      setLoadError(true);
      toast.error(apiErrorMessage(error, "Failed to load team roles"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role === "seller_admin") loadAll();
  }, [loadAll, user]);

  const refreshRoles = async () => {
    const res = await axios.get(`${API}/seller-roles`);
    const list = Array.isArray(res.data) ? res.data : res.data?.roles || [];
    setRoles(list);
    return list;
  };

  const selectRole = (role) => {
    setSelectedId(role.id);
    setForm({
      displayName: role.display_name || role.name,
      slug: role.name,
      slugTouched: true,
      description: role.description || "",
      permissions: new Set(role.permissions || []),
    });
  };

  const startCreate = () => {
    setSelectedId(NEW_ROLE);
    setForm(emptyForm());
  };

  const isCreate = selectedId === NEW_ROLE;
  const selectedRole = isCreate ? null : roles.find((r) => r.id === selectedId) || null;
  const isLocked = !!selectedRole && LOCKED_ROLE_NAMES.has(selectedRole.name);
  const isSystem = !!selectedRole?.is_system;
  const canEditMeta = isCreate || (!!selectedRole && !isSystem && !isLocked);
  const canEditPermissions = isCreate || (!!selectedRole && !isLocked);
  const assignedCount = selectedRole?.assigned_users ?? 0;
  const totalPermissions = catalog.reduce((n, g) => n + g.permissions.length, 0);
  const showPermissionGroups =
    isCreate || (selectedRole && (!isLocked || (selectedRole.permissions || []).length > 0));
  const slugError =
    isCreate && form.slug && !SLUG_RE.test(form.slug)
      ? "Lowercase letters, digits and underscores only; must start with a letter (2-50 chars)"
      : undefined;

  const onDisplayNameChange = (e) => {
    const value = e.target.value;
    setForm((prev) => ({
      ...prev,
      displayName: value,
      slug: prev.slugTouched ? prev.slug : slugify(value),
    }));
  };

  const togglePermission = (key, checked) => {
    setForm((prev) => {
      const permissions = new Set(prev.permissions);
      if (checked) permissions.add(key);
      else permissions.delete(key);
      return { ...prev, permissions };
    });
  };

  const toggleGroup = (group, checked) => {
    setForm((prev) => {
      const permissions = new Set(prev.permissions);
      group.permissions.forEach((p) => {
        if (checked) permissions.add(p.key);
        else permissions.delete(p.key);
      });
      return { ...prev, permissions };
    });
  };

  const handleSave = async () => {
    const displayName = form.displayName.trim();
    if (!displayName) {
      toast.error("Display name is required");
      return;
    }
    if (isCreate && !SLUG_RE.test(form.slug)) {
      toast.error("Identifier must be a slug: lowercase letters, digits, underscores (2-50 chars)");
      return;
    }
    setSaving(true);
    try {
      if (isCreate) {
        const res = await axios.post(`${API}/seller-roles`, {
          name: form.slug,
          display_name: displayName,
          description: form.description.trim(),
          permissions: [...form.permissions],
        });
        toast.success("Role created");
        const list = await refreshRoles();
        const created =
          list.find((r) => r.id === res.data?.id) || list.find((r) => r.name === form.slug);
        if (created) selectRole(created);
        else setSelectedId(null);
      } else {
        const payload = isSystem
          ? { permissions: [...form.permissions] }
          : {
              display_name: displayName,
              description: form.description.trim(),
              permissions: [...form.permissions],
            };
        await axios.put(`${API}/seller-roles/${selectedRole.id}`, payload);
        toast.success("Role updated");
        const list = await refreshRoles();
        const updated = list.find((r) => r.id === selectedRole.id);
        if (updated) selectRole(updated);
      }
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to save role"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/seller-roles/${selectedRole.id}`);
      toast.success("Role deleted");
      setShowDeleteConfirm(false);
      setSelectedId(null);
      await refreshRoles();
    } catch (error) {
      // 409 (users still assigned) and validation errors surface here
      toast.error(apiErrorMessage(error, "Failed to delete role"));
    } finally {
      setDeleting(false);
    }
  };

  const lockedNote = isLocked
    ? "This is the locked account-owner role - it always has every seller-portal permission and cannot be edited or deleted."
    : null;

  return (
    <SellerLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <PageHeader
          title="Team Roles"
          subtitle="Control exactly what each team member can do on your seller portal"
          actions={
            <Button onClick={startCreate} data-testid="new-role-button">
              <Plus className="mr-2 h-4 w-4" />
              New Role
            </Button>
          }
        />

        <Card className="border-0 bg-brand-bg/60 shadow-sm">
          <CardContent className="flex items-start gap-3 py-4">
            <Info className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
            <p className="text-sm text-slate-600">
              Roles apply to <strong>team members (seller users) only</strong> - the Seller Admin
              account always has full access. Create a role here, then assign it to a team member
              from the <strong>Users</strong> page.
            </p>
          </CardContent>
        </Card>

        {loading ? (
          <LoadingSkeleton variant="cards" rows={6} />
        ) : loadError ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="py-8">
              <EmptyState
                icon={KeyRound}
                title="Couldn't load team roles"
                description="Something went wrong while fetching your roles. Check your connection and try again."
                action={
                  <Button variant="outline" onClick={loadAll} data-testid="retry-load-roles">
                    Retry
                  </Button>
                }
              />
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(260px,320px)_1fr]">
            {/* Role list */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-heading">Roles</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {roles.length === 0 ? (
                  <EmptyState
                    icon={ShieldCheck}
                    title="No roles yet"
                    description="Create your first custom role to grant a tailored permission set to your team."
                    action={
                      <Button variant="outline" onClick={startCreate} data-testid="role-list-empty-new">
                        <Plus className="mr-2 h-4 w-4" />
                        New Role
                      </Button>
                    }
                  />
                ) : (
                  <div className="divide-y divide-slate-100">
                    {roles.map((role) => {
                      const locked = LOCKED_ROLE_NAMES.has(role.name);
                      const active = role.id === selectedId;
                      return (
                        <button
                          key={role.id}
                          type="button"
                          onClick={() => selectRole(role)}
                          data-testid={`role-list-item-${role.name}`}
                          className={cn(
                            "w-full border-l-2 border-transparent px-4 py-3 text-left transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                            active && "border-brand bg-brand-bg/60"
                          )}
                        >
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-medium text-slate-800">
                              {role.display_name || role.name}
                            </span>
                            {locked && <Lock className="h-3.5 w-3.5 text-slate-400" aria-label="Locked role" />}
                            {role.is_system && (
                              <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
                                System
                              </Badge>
                            )}
                          </div>
                          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-500">
                            <span className="font-mono">{role.name}</span>
                            <span className="inline-flex items-center gap-1">
                              <Users className="h-3 w-3" />
                              {role.assigned_users ?? 0}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Editor pane */}
            {!selectedId ? (
              <Card className="border-0 shadow-sm">
                <CardContent className="py-12">
                  <EmptyState
                    icon={ShieldCheck}
                    title="Select a role"
                    description="Pick a role from the list to view or edit its permissions, or create a new custom role."
                    action={
                      <Button variant="outline" onClick={startCreate} data-testid="editor-empty-new-role">
                        <Plus className="mr-2 h-4 w-4" />
                        New Role
                      </Button>
                    }
                  />
                </CardContent>
              </Card>
            ) : (
              <div className="min-w-0 space-y-6">
                <Card className="border-0 shadow-sm">
                  <CardHeader className="flex flex-col gap-4 space-y-0 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <CardTitle className="flex flex-wrap items-center gap-2 text-lg font-heading">
                        {isCreate ? "New custom role" : selectedRole?.display_name || selectedRole?.name}
                        {isLocked && <Lock className="h-4 w-4 text-slate-400" aria-label="Locked role" />}
                        {!isCreate && isSystem && <Badge variant="outline">System</Badge>}
                        {!isCreate && selectedRole && !isSystem && (
                          <Badge className="border-0 bg-brand-bg text-brand hover:bg-brand-bg">Custom</Badge>
                        )}
                      </CardTitle>
                      {!isCreate && selectedRole && (
                        <p className="mt-1 flex items-center gap-1 text-xs text-slate-500">
                          <Users className="h-3.5 w-3.5" />
                          {assignedCount} team member{assignedCount === 1 ? "" : "s"} assigned
                        </p>
                      )}
                    </div>
                    <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
                      {!isCreate && selectedRole && !isSystem && !isLocked && (
                        <Button
                          variant="outline"
                          className="text-red-600 hover:bg-red-50 hover:text-red-700"
                          onClick={() => setShowDeleteConfirm(true)}
                          disabled={saving}
                          data-testid="delete-role-button"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </Button>
                      )}
                      {(isCreate || !isLocked) && (
                        <Button onClick={handleSave} disabled={saving} data-testid="save-role-button">
                          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                          {isCreate ? "Create Role" : "Save Changes"}
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {lockedNote && (
                      <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                        <Lock className="mt-0.5 h-4 w-4 shrink-0" />
                        <span>{lockedNote}</span>
                      </div>
                    )}
                    {!isCreate && isSystem && !isLocked && (
                      <p className="text-xs text-slate-500">
                        System role: the name is fixed and it cannot be deleted, but its permissions can be adjusted.
                      </p>
                    )}
                    <div className={cn("grid grid-cols-1 gap-4", isCreate && "sm:grid-cols-2")}>
                      <FormField label="Display name" htmlFor="role-display-name" required={isCreate}>
                        <Input
                          id="role-display-name"
                          value={form.displayName}
                          onChange={onDisplayNameChange}
                          disabled={!canEditMeta}
                          placeholder="e.g. Dispatch Executive"
                          data-testid="role-display-name-input"
                        />
                      </FormField>
                      {isCreate && (
                        <FormField
                          label="Identifier"
                          htmlFor="role-slug"
                          required
                          hint="Lowercase letters, digits, underscores. Cannot be changed later."
                          error={slugError}
                        >
                          <Input
                            id="role-slug"
                            className="font-mono"
                            value={form.slug}
                            onChange={(e) =>
                              setForm((prev) => ({ ...prev, slug: e.target.value, slugTouched: true }))
                            }
                            placeholder="dispatch_executive"
                            data-testid="role-slug-input"
                          />
                        </FormField>
                      )}
                    </div>
                    <FormField label="Description" htmlFor="role-description">
                      <Textarea
                        id="role-description"
                        rows={2}
                        value={form.description}
                        onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                        disabled={!canEditMeta}
                        placeholder="What this role is for"
                        data-testid="role-description-input"
                      />
                    </FormField>
                  </CardContent>
                </Card>

                {showPermissionGroups && (
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <h2 className="text-base font-heading font-semibold text-slate-800">Permissions</h2>
                      <span className="text-xs text-slate-500 tabular-nums">
                        {form.permissions.size} of {totalPermissions} selected
                      </span>
                    </div>
                    {catalog.map((group) => (
                      <PermissionGroupCard
                        key={group.group}
                        group={group}
                        selected={form.permissions}
                        disabled={!canEditPermissions}
                        onToggleGroup={toggleGroup}
                        onTogglePermission={togglePermission}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title={`Delete role "${selectedRole?.display_name || selectedRole?.name}"?`}
        description={
          assignedCount > 0
            ? `${assignedCount} team member${assignedCount === 1 ? " is" : "s are"} currently assigned to this role. Reassign them before deleting - the server will reject the delete otherwise.`
            : "This permanently removes the role. This action cannot be undone."
        }
        confirmLabel="Delete Role"
        tone="destructive"
        loading={deleting}
        onConfirm={handleDelete}
      />
    </SellerLayout>
  );
};

export default SellerTeamRoles;
